<template>
  <v-row
    no-gutters
    justify="center"
  >
    <v-col
      xl="7"
      md="9"
      class="pt-6"
    >
      <div class="text-h5 grey--text text--darken-2 font-weight-light mb-6 d-inline-flex align-center">
        <v-btn
          icon
          :to="{ name: $routeNames.ADMIN_PLAYERS }"
          class="mr-5"
        >
          <v-icon small>
            fal fa-arrow-left
          </v-icon>
        </v-btn>
        {{ $t('new_player.dialog.header') }}
      </div>

      <validation-observer ref="validationObserver">
        <v-card
          class="pa-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.profile.title') }}
            </div>
            <div class="text-body-2 grey--text">
              {{ $t('new_player.profile.enter_details') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row
              no-gutters
            >
              <v-col
                class="pr-1"
                cols="auto"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    v-model="playerFormData.first_name"
                    :error-messages="errors"
                    :label="$t('text_input.data.first_name.title')"
                    name="name"
                    outlined
                    style="width: 375px"
                    append-icon="fa-light fa-address-card"
                  />
                </validation-provider>
              </v-col>
              <v-col class="pl-1">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    v-model="playerFormData.last_name"
                    :error-messages="errors"
                    :label="$t('text_input.data.last_name.title')"
                    name="last_name"
                    outlined
                    style="width: 375px"
                    append-icon="fa-light fa-address-card"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col
                cols="auto"
                class="pr-1"
              >
                <div class="text-body-2 v-label mb-2">
                  {{ $t('text_input.data.date_of_birth.title') }}
                </div>

                <div style="width: 380px">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <dob-input
                      v-model="playerFormData.birth_date"
                      :error-messages="errors"
                    />
                  </validation-provider>
                </div>
              </v-col>
              <v-col class="pl-1">
                <div class="text-body-2 v-label mb-2">
                  {{ $t('select.data.gender.title') }}
                </div>

                <div style="width: 370px">
                  <v-btn-toggle
                    v-model="playerFormData.gender"
                    color="primary"
                    mandatory
                    class="pt-1 v-btn-toggle--block"
                  >
                    <v-btn
                      value="male"
                      class="text-none"
                    >
                      <v-icon
                        left
                        small
                      >
                        fal fa-mars
                      </v-icon>
                      {{ $t('select.data.gender.male') }}
                    </v-btn>
                    <v-btn
                      value="female"
                      class="text-none"
                    >
                      <v-icon
                        left
                        small
                      >
                        fal fa-venus
                      </v-icon>
                      {{ $t('select.data.gender.female') }}
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </v-col>
            </v-row>

            <v-row
              align="center"
              no-gutters
            >
              <v-col
                cols="auto"
                class="pr-1"
              >
                <div class="text-body-2 v-label mb-2">
                  {{ $t('dropdown.select_team.title') }}
                </div>
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-select
                    v-model="playerFormData.team_id"
                    name="team_id"
                    :items="teams.data"
                    item-value="id"
                    item-text="name"
                    :label="$t('dropdown.select_team.label')"
                    outlined
                    style="width: 375px"
                    :error-messages="errors"
                    :placeholder="$t('dropdown.select_team.placeholder')"
                    @change="fetchFutureEvents($event); loadExternalPlayers()"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="auto"
                class="pl-1 mb-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                >
                  <number-input
                    v-model="playerFormData.height"
                    outlined
                    :label="$t('text_input.data.height.title')"
                    name="height"
                    placeholder="180"
                    suffix="cm"
                    :decimal-precision="1"
                    :error-messages="errors"
                    style="width: 185px"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="auto"
                class="pl-1 mb-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                >
                  <number-input
                    v-model="playerFormData.weight"
                    outlined
                    :label="$t('text_input.data.weight.title')"
                    name="weight"
                    placeholder="80"
                    :suffix="weightSuffix(user.organisation_settings.timezone)"
                    :decimal-precision="1"
                    :error-messages="errors"
                    style="width: 185px"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <validation-provider
              v-if="user.sport && !user.sport.is_individual"
              v-slot="{ errors }"
            >
              <v-select
                v-model="playerFormData.player_position_ids"
                :items="playerPositions.data"
                :loading="playerPositions.loading"
                :error="errors.length > 0"
                item-value="id"
                :item-text="(position) => position.name + ' (' + position.short_name + ')'"
                multiple
                chips
                small-chips
                deletable-chips
                name="player_position_ids"
                outlined
                :label="$t('table_headers.player_overview.position')"
                :placeholder="$t('dropdown.position.placeholder')"
                style="width: 375px"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              ref="imageErrors"
            >
              <div class="text-body-2 v-label mb-2">
                {{ $t('media_input.data.player_photo.title') }}
              </div>
              <image-crop
                v-model="playerFormData.photo_base_64"
                :quality="2"
                :width="250"
                :height="250"
                prevent-white-space
                :placeholder="$t('add_player_photo.placeholder')"
                accept="image/png,image/jpeg"
                :placeholder-font-size="12"
                :file-size-limit="2000000"
                @errors="$refs.imageErrors.setErrors($event)"
              />
              <div
                v-if="errors.length > 0"
                class="error--text caption"
              >
                {{ errors[0] }}
              </div>
            </validation-provider>
          </v-card-text>
        </v-card>

        <v-card
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <v-row>
              <v-col v-if="!onlyPassivePlayers">
                <div class="mb-1">
                  {{ $t('new_player.user_account.title') }}
                </div>
                <div class="text-body-2 grey--text">
                  {{ $t('new_player.user_account.enter_details') }}
                </div>
              </v-col>
              <v-col v-else>
                <div class="mb-1">
                  {{ $t("card_title.user_details") }}
                </div>
              </v-col>
              <v-col
                v-if="!onlyPassivePlayers"
                cols="auto"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-switch
                        v-model="playerFormData.create_user_account"
                        inset
                        class="d-inline-block pa-0 ma-0 font-weight-regular"
                        :label="$t('switch.create_user_account')"
                      />
                    </div>
                  </template>
                  <span>
                    {{ $t('switch.create_user_account.tooltip') }}
                  </span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="pt-5">
            <validation-provider
              v-if="playerFormData.create_user_account"
              v-slot="{ errors }"
              :rules="{ required: true, unique_username: { user_id: undefined }}"
            >
              <v-text-field
                v-model="playerFormData.username"
                :error-messages="errors"
                :label="$t('table_headers.admin_all_players.username')"
                name="username"
                :placeholder="$t('text_input.user.username.placeholder')"
                append-icon="fa-light fa-user"
                outlined
                style="width: 375px"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              :rules="{ required: !!playerFormData.create_user_account, email: true, unique_email: { user_id: undefined }}"
            >
              <v-text-field
                v-model="playerFormData.email"
                :error-messages="errors"
                :label="$t('table_headers.admin_staff.email')"
                name="email"
                outlined
                autocomplete="off"
                style="width: 375px"
                append-icon="fa-light fa-envelope"
              />
            </validation-provider>

            <div v-if="playerFormData.create_user_account">
              <v-switch
                v-model="manualPassword"
                :label="$t('switch.set_password_manually')"
                persistent-hint
                :hint="manualPassword ? $t('switch.set_password_manually.player.no_instructions') : $t('switch.set_password_manually.player.instructions')"
              />

              <div
                v-if="manualPassword"
                class="mt-5"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="password"
                  rules="required|password_policy"
                >
                  <password-field
                    ref="password"
                    v-model="playerFormData.password"
                    :error-messages="errors"
                    :label="$t('text_input.login.password.title')"
                    outlined
                    type="password"
                    style="width: 375px"
                    append-icon="fa-light fa-key"
                  />
                </validation-provider>

                <validation-provider
                  v-slot="{ errors }"
                  rules="required|confirmed:password"
                >
                  <password-field
                    v-model="playerFormData.password_repeat"
                    :error-messages="errors"
                    :label="$t('text_input.user.repeat_password.title')"
                    outlined
                    type="password"
                    style="width: 375px"
                    append-icon="fa-light fa-key"
                  />
                </validation-provider>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasCatapultIntegration || hasPolarTeamIntegration"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title>
            <div class="mb-1">
              {{ $t('new_player.integrations.title') }}
            </div>
            <div class="text-body-2 grey--text d-block">
              {{ $t('new_player.integrations.your_organisation') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <div v-if="hasCatapultIntegration">
              <validation-provider>
                <v-autocomplete
                  v-model="playerFormData.catapult_id"
                  :items="externalPlayers.data"
                  :loading="externalPlayers.loading"
                  validation-provider
                  outlined
                  style="width: 375px"
                  :hint="!playerFormData.team_id ? $t('dropdown.integrations.please_select.instruction') : undefined"
                  :persistent-hint="!playerFormData.team_id"
                  :label="$t('dropdown.select_catapult_player.title')"
                  item-value="id"
                  item-text="last_name"
                >
                  <template v-slot:item="{ item: externalPlayer }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ externalPlayer.last_name }}, {{ externalPlayer.first_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ externalPlayer.jersey }} &middot; {{ externalPlayer.id }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.last_name }}, {{ item.first_name }}
                  </template>
                </v-autocomplete>
              </validation-provider>
            </div>
            <div v-if="hasPolarTeamIntegration">
              <validation-provider>
                <v-autocomplete
                  v-model="playerFormData.polar_team_id"
                  :items="externalPolarTeamPlayers.data"
                  :loading="externalPolarTeamPlayers.loading"
                  validation-provider
                  outlined
                  style="width: 375px"
                  clearable
                  :label="$t('dropdown.select_polar_player.title')"
                  item-value="player_id"
                  item-text="last_name"
                >
                  <template v-slot:item="{ item: externalPlayer }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ externalPlayer.last_name }}, {{ externalPlayer.first_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ externalPlayer.player_number }} &middot; {{ externalPlayer.player_id }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.last_name }}, {{ item.first_name }}
                  </template>
                </v-autocomplete>
              </validation-provider>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="hasMembershipModule && $can('edit-player-membership')"
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title>
            <div>
              <div class="mb-1">
                {{ $t('new_player.membership_plan.title') }}
              </div>
              <div class="text-body-2 grey--text d-block">
                {{ $t('new_player.membership_plan.set_membership') }}
              </div>
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-autocomplete
              v-model="playerFormData.membership_plan_id"
              :items="membershipPlans.data"
              :loading="membershipPlans.loading"
              outlined
              style="width: 375px"
              item-value="id"
              item-text="title"
              :label="$t('dropdown.select_membership.title')"
            >
              <template v-slot:item="{ item: membershipPlan }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ membershipPlan.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ membershipPlan.currency }} {{ Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(membershipPlan.price) }} /
                    {{ membershipPlanIntervals[membershipPlan.interval].pricePer }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <div v-if="selectedMembershipPlan">
              <v-switch
                v-model="playerFormData.membership_recurring"
                class="ma-0"
                :label="$t('new_player.membership_plan.recurring.label')"
              />

              <v-menu
                v-model="membershipStartDateSelectorActive"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on }">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :value="playerFormData.membership_start_date ? $dayjs(playerFormData.membership_start_date).format('ll') : ''"
                      :label="$t('new_player.membership_plan.select_start_date')"
                      outlined
                      :error-messages="errors"
                      style="width: 375px"
                      :hint="$t('new_player.membership_plan.select_start_date.hint')"
                      v-on="on"
                    />
                  </validation-provider>
                </template>
                <v-date-picker
                  v-model="playerFormData.membership_start_date"
                  :first-day-of-week="1"
                  no-title
                  @change="membershipStartDateSelectorActive = false"
                />
              </v-menu>
            </div>

            <validation-provider
              v-slot="{ errors }"
              rules="email"
            >
              <v-text-field
                v-model="playerFormData.billing_email"
                :error-messages="errors"
                :label="$t('text_input.parent_email.title')"
                outlined
                :hint="$t('text_input.parent_email.hint')"
                style="width: 375px"
              />
            </validation-provider>
          </v-card-text>
        </v-card>

        <v-card
          class="pa-3 mt-3"
          rounded="md"
        >
          <v-card-title class="d-block">
            <div class="mb-1">
              {{ $t('new_player.device.title') }}
            </div>
            <div class="text-body-2 grey--text d-block">
              {{ $t('new_player.device.warning.devices_data') }}
            </div>
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="auto">
                <validation-provider v-slot="{ errors }">
                  <v-autocomplete
                    v-model="playerFormData.device_id"
                    :items="devices.data"
                    item-text="device_name"
                    item-value="id"
                    :error-messages="errors"
                    :label="$t('dropdown.serial_number.title')"
                    outlined
                    style="width: 160px"
                    :placeholder="$t('dropdown.device_id.placeholder')"
                    append-icon="fa-light fa-microchip"
                    dense
                  >
                    <template v-slot:item="{ item: device }">
                      <v-list-item-content>
                        <v-list-item-title :class="{ 'grey--text': !!device.player_id }">
                          {{ device.device_name }} &middot; {{ device.serial_number }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span v-if="device.player">
                            {{ device.player.first_name }} {{ device.player.last_name }}
                          </span>
                          <span v-else>
                            Free
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </validation-provider>
              </v-col>
              <v-col
                cols="auto"
              >
                <v-btn
                  color="primary"
                  class="text-none"
                  text
                  @click="connectNewDevice()"
                >
                  {{ $t('button.connect_new') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row
          no-gutters
          justify="end"
          class="mt-5 mb-12"
        >
          <v-col cols="auto">
            <v-btn
              text
              color="grey"
              large
              class="mr-2"
              :to="{ name: $routeNames.ADMIN_PLAYERS }"
            >
              {{ $t('button.cancel') }}
            </v-btn>
            <v-btn
              large
              outlined
              :loading="submitting"
              color="primary"
              class="mr-2"
              @click="submit(true)"
            >
              {{ $t("button.save_add_new") }}
            </v-btn>
            <v-btn
              large
              color="primary"
              :loading="submitting"
              @click="submit()"
            >
              {{ $t("button.save_player") }}
            </v-btn>
          </v-col>
        </v-row>
      </validation-observer>

      <card-dialog
        v-model="addToFutureEventsDialog"
        max-width="600px"
        :title="$t('card_dialog.add_to_upcoming_events.title')"
        :persistent="false"
      >
        <v-card-text
          class="pa-5"
        >
          {{ $t("card_dialog.add_to_upcoming_events.label") }} <strong> {{ $tc("card_dialog.add_to_upcoming_events.num_of_events", teamFutureEvents, {num: teamFutureEvents}) }}</strong>?
        </v-card-text>

        <v-divider />

        <template v-slot:actions>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              color="grey"
              class="text-none"
              @click="addToFutureEvents = false; submit(addNewPlayerAfterSaving)"
            >
              {{ $t("card_dialog.add_to_upcoming_events.button.no") }}
            </v-btn>
            <v-btn
              color="primary"
              class="text-none"
              @click="addToFutureEvents = true; submit(addNewPlayerAfterSaving)"
            >
              <v-icon
                small
                left
              >
                fa fa-check
              </v-icon>
              {{ $t("card_dialog.add_to_upcoming_events.button.yes") }}
            </v-btn>
          </v-card-actions>
        </template>
      </card-dialog>
    </v-col>
  </v-row>
</template>

<script>
import DobInput from '@/components/helpers/DobInput'
import { EventApiService, PlayerApiService, PlayerPositionApiService, TeamApiService } from '@/data/services'
import { cloneDeep } from 'lodash'
import { DeviceApiService } from '@/data/services/DeviceApiService'
import { EventBus } from '@/plugins/bus'
import { MembershipPlanApiService } from '@/data/services/MembershipPlanApiService'
import ImageCrop from '@/components/inputs/ImageCrop.vue'
import { MembershipPlanIntervals } from '@/data/models/MembershipPlan'
import dayjs from 'dayjs'
import CardDialog from '@/components/helpers/CardDialog.vue'
import PasswordField from '@/components/inputs/PasswordField.vue'
import NumberInput from '@/components/helpers/NumberInput.vue'
import { weightSuffix } from '@/components/training-builder/helpers'
import { toCamelCase } from '@/utils/helpers'

const emptyPlayerFormData = {
  id: null,
  first_name: null,
  last_name: null,
  gender: null,
  dob: {
    day: null,
    month: null,
    year: null
  },
  birth_date: null,
  height: null,
  weight: null,
  pin: null,
  birth_city: null,
  address: null,
  phone: null,
  parents: null,
  school: null,
  team_id: null,
  sport_details: null,
  registration_number: null,
  player_position_id: null,
  player_status_id: null,
  username: null,
  email: null,
  billing_email: null,
  photo: null,
  password: null,
  password_repeat: null,
  external_id: null,
  catapult_id: null,
  polar_team_id: null,
  device_id: null,
  membership_plan_id: null,
  membership_recurring: true,
  membership_start_date: null,
  membership_renewal_day: null,
  photo_base_64: null,
  create_user_account: true,
  player_position_ids: []
}

export default {
  name: 'AdminPlayerForm',
  components: { NumberInput, PasswordField, CardDialog, ImageCrop, DobInput },
  data: () => ({
    playerFormData: cloneDeep(emptyPlayerFormData),
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false
    },
    teams: {
      data: [],
      loading: false
    },
    externalPlayers: {
      loading: false,
      data: []
    },
    externalPolarTeamPlayers: {
      loading: false,
      data: []
    },
    devices: {
      loading: false,
      data: []
    },
    membershipPlans: {
      loading: false,
      data: []
    },
    manualPassword: false,
    submitting: false,
    membershipStartDateSelectorActive: false,
    teamFutureEvents: 0,
    addToFutureEventsDialog: false,
    addToFutureEvents: false,
    addNewPlayerAfterSaving: false
  }),
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasMembershipModule() {
      return this.user.organisation_settings.has_membership_module
    },
    hasCatapultIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'catapult')
    },
    hasPolarTeamIntegration() {
      return (this.user.organisation_settings.integrations || []).some(i => i.key === 'polar-team')
    },
    onlyPassivePlayers() {
      return this.user.organisation_settings.only_passive_players ?? false
    },
    selectedMembershipPlan() {
      if (!this.playerFormData.membership_plan_id) return undefined

      return this.membershipPlans.data.find(mp => mp.id === this.playerFormData.membership_plan_id)
    }
  },
  created() {
    this.membershipPlanIntervals = MembershipPlanIntervals

    this.fetchTeams()
    this.fetchPlayerPositions()
    this.fetchDevices()
    this.fetchMembershipPlans()

    this.loadExternalPolarTeamPlayers()

    EventBus.$on([this.$events.DEVICE_CREATED, this.$events.DEVICE_UPDATED], (event) => {
      this.fetchDevices()
    })

    if (this.onlyPassivePlayers) this.playerFormData.create_user_account = false

    this.playerFormData.membership_start_date = this.$dayjs().startOf('month').format('YYYY-MM-DD')
  },
  methods: {
    weightSuffix,
    async fetchTeams() {
      try {
        this.teams.loading = true

        const response = await TeamApiService.getAll()

        this.teams.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teams.loading = false
      }
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short_name: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchDevices() {
      try {
        this.devices.loading = true

        const response = await DeviceApiService.getAll()

        this.devices.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_devices.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.devices.loading = false
      }
    },
    async fetchMembershipPlans() {
      try {
        this.membershipPlans.loading = true

        const result = await MembershipPlanApiService.getAll()

        this.membershipPlans.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_membership_plans.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.membershipPlans.loading = false
      }
    },
    async loadExternalPlayers() {
      if (!this.hasCatapultIntegration) return

      try {
        this.externalPlayers.loading = true

        this.externalPlayers.data = await PlayerApiService.getExternalPlayers(this.playerFormData.team_id)
      } catch (e) {

      } finally {
        this.externalPlayers.loading = false
      }
    },
    async loadExternalPolarTeamPlayers() {
      if (!this.hasPolarTeamIntegration) return

      try {
        this.externalPolarTeamPlayers.loading = true

        this.externalPolarTeamPlayers.data = await PlayerApiService.getExternalPolarTeamPlayers()
      } catch (e) {

      } finally {
        this.externalPolarTeamPlayers.loading = false
      }
    },
    async submit(addNew = false) {
      const validation = await this.$refs.validationObserver.validate()

      if (!validation) {
        this.$vuetify.goTo('.error--text')
        return
      }

      if (this.teamFutureEvents > 0 && !this.addToFutureEventsDialog) {
        this.addNewPlayerAfterSaving = addNew
        this.addToFutureEventsDialog = true
        return
      }

      this.addToFutureEventsDialog = false

      await this.save(addNew)
    },
    async save(addNew = false) {
      try {
        this.submitting = true

        const data = this.playerFormData

        if (!this.manualPassword) {
          data.password = undefined
          data.password_repeat = undefined
        }

        data.push_to_upcoming_events = this.addToFutureEvents

        const result = await PlayerApiService.create(data)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_save.success'),
          color: 'success'
        })

        this.$refs.validationObserver.reset()

        EventBus.$emit(this.$events.PLAYER_CREATED, result.data)

        if (addNew) {
          this.playerFormData = cloneDeep(emptyPlayerFormData)
          this.$vuetify.goTo(0)
        } else {
          this.$router.push({ name: this.$routeNames.ADMIN_PLAYER_EDIT, params: { id: result.data.id } })
        }
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.adding_player.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    connectNewDevice() {
      EventBus.$emit(this.$events.OPEN_DIALOG_DEVICE)
    },
    async fetchFutureEvents(teamId) {
      this.teamFutureEvents = 0
      this.addToFutureEvents = false

      try {
        const queryParams = {
          from: dayjs().format('YYYY-MM-DD'),
          team_id: teamId,
          per_page: 1,
          page: 1
        }

        const result = await EventApiService.getPaginated(queryParams)

        if (result.meta.total > 0) this.teamFutureEvents = result.meta.total
      } catch (e) {

      }
    }
  }
}
</script>
